import React, { useState, useEffect } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { toast } from "react-toastify";
import moment from "moment";
import "react-tabs/style/react-tabs.css";
import "../styles/OrdersView.css";
import apiService from "../services/apiService";
import EditCoordinatesModal from "./EditCoordinatesModal";

const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === "true";

const SOURCE_LABEL = {
  ORIGINAL:         { text: 'Pendiente',        icon: '⏳', className: 'source-pending' },
  UPDATE_AUTOMATIC: { text: 'Google',           icon: '🤖', className: 'source-auto' },
  UPDATE_MANUAL:    { text: 'Manual',           icon: '✏️', className: 'source-manual' },
};

const fmtPurchaseDateTime = (dt) => {
  if (!dt) return null;
  try {
    const d = new Date(dt);
    const date = d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', timeZone: 'America/Lima' });
    const time = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Lima' });
    return `${date} ${time}`;
  } catch { return null; }
};

const OrdersView = ({ selectedDate }) => {
  const [orders, setOrders] = useState([]);
  const [groupedByTime, setGroupedByTime] = useState({});
  const [groupedByDistrict, setGroupedByDistrict] = useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [selectedDate]);

  const applyOrders = (rawOrders) => {
    setOrders(rawOrders);

    const byTime = {};
    rawOrders.forEach((order) => {
      const timeSlot = `${order.hour_programmed_start}-${order.hour_programmed_end}`;
      if (!byTime[timeSlot]) byTime[timeSlot] = [];
      byTime[timeSlot].push(order);
    });
    setGroupedByTime(byTime);

    const byDistrict = {};
    rawOrders.forEach((order) => {
      if (!byDistrict[order.district]) byDistrict[order.district] = [];
      byDistrict[order.district].push(order);
    });
    setGroupedByDistrict(byDistrict);
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      console.log("🌐 Llamando a la API...");
      const ordersData = await apiService.getPurchasesByDate(selectedDate);
      toast.success(`${ordersData.length} pedidos cargados`);
      applyOrders(ordersData);

      // Validación automática en segundo plano — no bloquea la vista
      triggerAutoValidation();
    } catch (error) {
      console.error("Error cargando pedidos:", error);
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 422)
      ) {
        console.log("No autenticado, redirigiendo...");
      } else {
        toast.error("Error al cargar pedidos");
      }
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerAutoValidation = async () => {
    setValidating(true);
    try {
      const result = await apiService.validateAddressesForDate(selectedDate);
      if (result && result.results && result.results.updated > 0) {
        toast.info(
          `📍 ${result.results.updated} dirección(es) corregida(s) automáticamente con Google Places`,
          { autoClose: 5000 }
        );
        // Recargar pedidos para mostrar los flags actualizados
        const updated = await apiService.getPurchasesByDate(selectedDate);
        applyOrders(updated);
      }
    } catch (err) {
      // No mostrar error al usuario — es un proceso silencioso
      console.warn("Validación Places:", err.message);
    } finally {
      setValidating(false);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prev) => {
      if (prev.includes(orderId)) {
        return prev.filter((id) => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };


  const handleEditCoordinates = (order, e) => {
    e.stopPropagation();
    setEditingOrder(order);
  };

  const handleCloseModal = () => {
    setEditingOrder(null);
  };

  const handleCoordinatesUpdated = () => {
    // Recargar pedidos para reflejar las coordenadas actualizadas
    loadOrders();
  };

  const renderOrderCard = (order) => {
    const missingCoords = !order.latitude || !order.longitude;
    return (
    <div
      key={order.id}
      className={`order-card ${selectedOrders.includes(order.id) ? "selected" : ""} ${missingCoords ? "no-coords" : ""}`}
      onClick={() => toggleOrderSelection(order.id)}
    >
      <div className='order-header'>
        <input
          type='checkbox'
          checked={selectedOrders.includes(order.id)}
          onChange={() => toggleOrderSelection(order.id)}
          onClick={(e) => e.stopPropagation()}
        />
        <span className='order-id'>#{order.id}</span>
        {missingCoords && (
          <span className='no-coords-badge'>Sin GPS</span>
        )}
        <span
          className={`order-status status-${(order.status_delivery || order.status || "").toLowerCase().replace(/ /g, "-")}`}
        >
          {order.status_delivery || order.status || "PENDIENTE"}
        </span>
      </div>

      <div className='order-body'>
        <div className='order-customer'>
          <strong>{order.customer_name || "Cliente"}</strong>
        </div>
        <div className='order-address'>
          <i className='icon-location'></i>
          {order.full_address || "Sin dirección"}
        </div>
        <div className='order-time'>
          <i className='icon-clock'></i>
          {order.hour_programmed_start || "--:--"} -{" "}
          {order.hour_programmed_end || "--:--"}
        </div>
        {fmtPurchaseDateTime(order.purchase_created_at) && (
          <div className='order-purchase-time'>
            🛒 Pedido: {fmtPurchaseDateTime(order.purchase_created_at)}
          </div>
        )}
        {order.leave_your_order && (
          <div className='order-delivery-info'>
            <i className='icon-info'></i>
            {order.leave_your_order}
          </div>
        )}
        <div className='order-coords'>
          {order.latitude && order.longitude ? (
            <span className='coords-badge success'>
              {Number(order.latitude).toFixed(4)}, {Number(order.longitude).toFixed(4)}
            </span>
          ) : (
            <span className='coords-badge warning'>Sin coordenadas</span>
          )}
          {SOURCE_LABEL[order.coord_update_source] && (
            <span className={`coords-badge ${SOURCE_LABEL[order.coord_update_source].className}`}>
              {SOURCE_LABEL[order.coord_update_source].icon}{' '}
              {SOURCE_LABEL[order.coord_update_source].text}
            </span>
          )}
          <span className={`coords-badge ${order.coords_validated === 'VALIDATED' ? 'validated' : 'not-validated'}`}>
            {order.coords_validated === 'VALIDATED' ? '✅ Validada' : '⚠️ No validada'}
          </span>
        </div>
      </div>

      <div className='order-footer'>
        <button
          className='btn-edit-coords'
          onClick={(e) => handleEditCoordinates(order, e)}
        >
          📍 {order.latitude && order.longitude ? 'Editar GPS' : 'Agregar GPS'}
        </button>
      </div>
    </div>
    );
  };

  return (
    <div className='orders-view'>
      <div className='view-header'>
        <h2>
          Pedidos del Día: {moment(selectedDate).format("DD/MM/YYYY")}
          {USE_MOCK_DATA && (
            <span
              style={{ fontSize: "14px", color: "#f39c12", marginLeft: "10px" }}
            >
              (Datos Mock)
            </span>
          )}
        </h2>
        <div className='view-stats'>
          <div className='stat-card'>
            <span className='stat-label'>Total Pedidos</span>
            <span className='stat-value'>{orders.length}</span>
          </div>
          <div className='stat-card'>
            <span className='stat-label'>Seleccionados</span>
            <span className='stat-value'>{selectedOrders.length}</span>
          </div>
          <div className='stat-card'>
            <span className='stat-label'>Con GPS</span>
            <span className='stat-value'>
              {orders.filter((o) => o.latitude && o.longitude).length}
            </span>
          </div>
        </div>
        {validating && (
          <div className='validation-banner'>
            🔍 Validando coordenadas con Google Places...
          </div>
        )}
      </div>


      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: "48px" }}>⏳</div>
          <p style={{ color: "#7f8c8d", marginTop: "20px" }}>
            Cargando pedidos...
          </p>
        </div>
      ) : (
        <Tabs className='orders-tabs'>
          <TabList>
            <Tab>Todos ({orders.length})</Tab>
            <Tab>Por Horario ({Object.keys(groupedByTime).length})</Tab>
            <Tab>Por Distrito ({Object.keys(groupedByDistrict).length})</Tab>
          </TabList>

          <TabPanel>
            <div className='orders-grid'>
              {orders.map((order) => renderOrderCard(order))}
            </div>
          </TabPanel>

          <TabPanel>
            <div className='grouped-orders'>
              {Object.entries(groupedByTime).map(([timeSlot, timeOrders]) => (
                <div key={timeSlot} className='group-section'>
                  <h3 className='group-title'>
                    <i className='icon-clock'></i>
                    {timeSlot} ({timeOrders.length} pedidos)
                  </h3>
                  <div className='orders-grid'>
                    {timeOrders.map((order) => renderOrderCard(order))}
                  </div>
                </div>
              ))}
            </div>
          </TabPanel>

          <TabPanel>
            <div className='grouped-orders'>
              {Object.entries(groupedByDistrict).map(
                ([district, districtOrders]) => (
                  <div key={district} className='group-section'>
                    <h3 className='group-title'>
                      <i className='icon-location'></i>
                      {district} ({districtOrders.length} pedidos)
                    </h3>
                    <div className='orders-grid'>
                      {districtOrders.map((order) => renderOrderCard(order))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </TabPanel>
        </Tabs>
      )}

      {editingOrder && (
        <EditCoordinatesModal
          order={editingOrder}
          onClose={handleCloseModal}
          onSuccess={handleCoordinatesUpdated}
          allowManual
        />
      )}
    </div>
  );
};

export default OrdersView;
